import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCode, FiSmartphone, FiGlobe, FiDatabase, FiCloud, FiShield } = FiIcons;

const Services = () => {
  const services = [
    {
      icon: FiGlobe,
      title: 'Web Applications',
      description: 'Responsive web apps built with modern frameworks like React, Vue, or Angular.',
      features: ['Progressive Web Apps', 'Real-time Features', 'API Integration']
    },
    {
      icon: FiSmartphone,
      title: 'Mobile Apps',
      description: 'Cross-platform mobile applications for iOS and Android using React Native or Flutter.',
      features: ['Native Performance', 'Push Notifications', 'Offline Capabilities']
    },
    {
      icon: FiDatabase,
      title: 'Backend APIs',
      description: 'Scalable backend services and RESTful APIs to power your applications.',
      features: ['Database Design', 'Authentication', 'Third-party Integrations']
    },
    {
      icon: FiCloud,
      title: 'Cloud Deployment',
      description: 'Deploy your MVP on reliable cloud platforms with automatic scaling.',
      features: ['AWS/Azure/GCP', 'CI/CD Pipelines', 'Monitoring & Analytics']
    },
    {
      icon: FiCode,
      title: 'Custom Solutions',
      description: 'Tailored software solutions for unique business requirements and workflows.',
      features: ['Custom Integrations', 'Automation Tools', 'Legacy Modernization']
    },
    {
      icon: FiShield,
      title: 'Security & Testing',
      description: 'Comprehensive testing and security measures to ensure your MVP is production-ready.',
      features: ['Security Audits', 'Performance Testing', 'Quality Assurance']
    }
  ];

  return (
    <section id="services" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Our MVP Development Services
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From concept to deployment, we offer comprehensive services to bring your MVP to life in record time.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-shadow duration-300"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <SafeIcon icon={service.icon} className="w-6 h-6 text-blue-600" />
              </div>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                {service.title}
              </h3>
              
              <p className="text-gray-600 mb-6">
                {service.description}
              </p>
              
              <ul className="space-y-2">
                {service.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center text-sm text-gray-700">
                    <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mr-3"></div>
                    {feature}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">
              Ready to Build Your MVP?
            </h3>
            <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
              Let's discuss your project and create a custom development plan that fits your timeline and budget.
            </p>
            <button className="bg-white text-blue-600 px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors duration-200 font-semibold">
              Schedule a Free Consultation
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;